// Baseline survey — mirrors /baseline-survey.md.
// Stored as JSONB on the backend; the frontend supplies the structure.

import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { api } from "../lib/api";

type Answers = {
  q1_tools: string[];
  q2_frequency: string;
  q3_top_use_cases: string;
  q4_attitude: string;
  q5_support_score: number;
  q6_biggest_concern: string;
  q7_tier_awareness: string;
  q8_pii_safe: string;
  q9_skill: string;
  q10_champion: string;
  q11_automate: string;
};

const Q1_TOOLS = ["ChatGPT", "Claude", "GitHub Copilot", "Perplexity", "Midjourney/DALL-E", "None"];
const Q2 = ["Multiple times a day","Once a day","A few times a week","Rarely","Never"];
const Q4 = ["Very Excited","Optimistic","Neutral","Apprehensive","Skeptical"];
const Q6 = ["Data privacy/security","Accuracy/hallucinations","Loss of human touch","Learning curve","Job security","No concerns"];
const Q7 = ["Yes, I understand it","I've heard of it but need a refresher","No, this is new to me"];
const Q9 = ["Prompt engineering","Data analysis","Building agents/automations","Image/media generation","Security/privacy"];
const Q10 = ["Yes","Maybe later","No thanks"];

const initial: Answers = {
  q1_tools: [],
  q2_frequency: "",
  q3_top_use_cases: "",
  q4_attitude: "",
  q5_support_score: 3,
  q6_biggest_concern: "",
  q7_tier_awareness: "",
  q8_pii_safe: "False",
  q9_skill: "",
  q10_champion: "",
  q11_automate: "",
};

export function Baseline() {
  const [a, setA] = useState<Answers>(initial);
  const [done, setDone] = useState(false);

  const submit = useMutation({
    mutationFn: () => api.intake.submitBaseline(a as unknown as Record<string, unknown>),
    onSuccess: () => setDone(true),
  });

  if (done) {
    return (
      <div className="space-y-2 max-w-xl">
        <h1 className="text-xl font-semibold">Thanks — baseline recorded.</h1>
        <p className="text-sm text-slate-600">You earned +10 XP toward your Novice level.</p>
      </div>
    );
  }

  const toggle = (key: "q1_tools", v: string) => {
    setA((s) => ({
      ...s,
      [key]: s[key].includes(v) ? s[key].filter((x) => x !== v) : [...s[key], v],
    }));
  };

  return (
    <form
      className="space-y-6 max-w-xl"
      onSubmit={(e) => { e.preventDefault(); submit.mutate(); }}
    >
      <header>
        <h1 className="text-xl font-semibold">Baseline AI Survey</h1>
        <p className="text-sm text-slate-600">~5 minutes. One-time, captures your starting point.</p>
      </header>

      <Section title="1. Which AI tools have you used for work in the last 30 days?">
        <Multi options={Q1_TOOLS} selected={a.q1_tools} onToggle={(v) => toggle("q1_tools", v)} />
      </Section>

      <Section title="2. On average, how often do you use AI for work tasks?">
        <Radio name="q2" options={Q2} value={a.q2_frequency} onChange={(v) => setA({ ...a, q2_frequency: v })} />
      </Section>

      <Section title="3. Your top 3 most frequent AI use cases">
        <textarea
          rows={3}
          className="w-full border rounded px-2 py-1 text-sm"
          value={a.q3_top_use_cases}
          onChange={(e) => setA({ ...a, q3_top_use_cases: e.target.value })}
        />
      </Section>

      <Section title="4. Your attitude toward AI adoption at our company">
        <Radio name="q4" options={Q4} value={a.q4_attitude} onChange={(v) => setA({ ...a, q4_attitude: v })} />
      </Section>

      <Section title='5. "I feel supported by the company to experiment with AI tools." (1–5)'>
        <input
          type="range" min={1} max={5}
          value={a.q5_support_score}
          onChange={(e) => setA({ ...a, q5_support_score: Number(e.target.value) })}
        />
        <span className="text-sm ml-2">{a.q5_support_score}/5</span>
      </Section>

      <Section title="6. Biggest concern regarding AI at work">
        <Radio name="q6" options={Q6} value={a.q6_biggest_concern} onChange={(v) => setA({ ...a, q6_biggest_concern: v })} />
      </Section>

      <Section title='7. Awareness of the 3-Tier Data Rule'>
        <Radio name="q7" options={Q7} value={a.q7_tier_awareness} onChange={(v) => setA({ ...a, q7_tier_awareness: v })} />
      </Section>

      <Section title='8. True or False: It is safe to paste raw customer PII into free ChatGPT'>
        <Radio name="q8" options={["True","False"]} value={a.q8_pii_safe} onChange={(v) => setA({ ...a, q8_pii_safe: v })} />
      </Section>

      <Section title="9. Skill you'd most like to learn this quarter">
        <Radio name="q9" options={Q9} value={a.q9_skill} onChange={(v) => setA({ ...a, q9_skill: v })} />
      </Section>

      <Section title='10. Interested in being an "AI Champion" for your department?'>
        <Radio name="q10" options={Q10} value={a.q10_champion} onChange={(v) => setA({ ...a, q10_champion: v })} />
      </Section>

      <Section title="11. If you could automate one repetitive task in your week right now, what would it be?">
        <textarea
          rows={2}
          className="w-full border rounded px-2 py-1 text-sm"
          value={a.q11_automate}
          onChange={(e) => setA({ ...a, q11_automate: e.target.value })}
        />
      </Section>

      {submit.error ? <p className="text-sm text-red-700">{(submit.error as Error).message}</p> : null}

      <button
        type="submit"
        disabled={submit.isPending}
        className="px-4 py-2 rounded bg-blue-600 text-white disabled:opacity-50"
      >
        {submit.isPending ? "Submitting…" : "Submit baseline"}
      </button>
    </form>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <fieldset className="space-y-2">
      <legend className="font-medium text-sm">{title}</legend>
      {children}
    </fieldset>
  );
}

function Radio({ name, options, value, onChange }: { name: string; options: string[]; value: string; onChange: (v: string) => void }) {
  return (
    <div className="space-y-1">
      {options.map((o) => (
        <label key={o} className="block text-sm">
          <input
            type="radio"
            name={name}
            className="mr-2"
            checked={value === o}
            onChange={() => onChange(o)}
          />
          {o}
        </label>
      ))}
    </div>
  );
}

function Multi({ options, selected, onToggle }: { options: string[]; selected: string[]; onToggle: (v: string) => void }) {
  return (
    <div className="space-y-1">
      {options.map((o) => (
        <label key={o} className="block text-sm">
          <input
            type="checkbox"
            className="mr-2"
            checked={selected.includes(o)}
            onChange={() => onToggle(o)}
          />
          {o}
        </label>
      ))}
    </div>
  );
}
